import { useLayoutEffect, useRef, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  FileText,
  Send,
  CheckCircle,
  AlertTriangle,
  Pencil,
  Download,
  Printer,
  Clock,
  Users,
} from 'lucide-react';
import { inspectionService } from '../../../services/inspectionService';
import { PdfPages } from '../../../components/documents/PdfPages';
import {
  clientSignatureLine,
  type ClientSignatureSummary,
} from '../clientSignature';
import { formatCountdown, sendsAtLabel, useCountdown } from '../sendHold';
import { ReportRecipientsEditor } from './ReportRecipientsEditor';
import { SendConfirmSheet } from './SendConfirmSheet';

type SendResult = {
  success: boolean;
  message: string;
  recipients?: Array<{ email: string; name: string; status: string }>;
  /** JIM-0922-01: who was asked to sign the report, and whether they have. */
  clientSignature?: ClientSignatureSummary | null;
} | null;

type ReportPhaseProps = {
  submittedInspectionId: string;
  /** JIM-1027-13: the site whose contact list the sheet and the hold can open in place. */
  siteId?: string;
  organizationId?: string | null;
  isGeneratingReport: boolean;
  reportUrl: string | null;
  reportError: string | null;
  isSendingReport: boolean;
  sendResult: SendResult;
  /** JIM-1003-05: the report is waiting out its ten minutes before it is
   * emailed. Edit stops the clock (`stopSend`); Done leaves it to send. */
  sendHold?: { sendsAt: string } | null;
  stopSend?: () => Promise<boolean>;
  // Final-inspection chain (Jim 2026-08-20): when the inspector declared
  // this the final inspection, the send also asked the consultant to
  // confirm — tell the inspector that happened.
  isFinalInspection?: boolean;
  generateReport: () => void;
  approveAndSend: () => void;
  onBackToDraft: () => void;
  onDone: (state: {
    success: boolean;
    inspectionId: string;
    message: string;
  }) => void;
  onSkip: (state: {
    success: boolean;
    inspectionId: string;
    message: string;
  }) => void;
};

const STAGES = ['Complete', 'Review', 'Send to Client'] as const;

/** The report box never shrinks below this; on a tiny window the page scrolls. */
export const MIN_REPORT_HEIGHT = 240;

function StageStrip({
  sent,
  sending = false,
}: {
  sent: boolean;
  sending?: boolean;
}) {
  // While the report waits out its hold the third step is the current one.
  const current = sending ? 2 : 1;
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs font-medium">
      {STAGES.map((label, i) => {
        const state: 'done' | 'current' | 'next' =
          sent || i < current ? 'done' : i === current ? 'current' : 'next';
        return (
          <li key={label} className="flex items-center gap-2">
            {i > 0 && (
              <span className="w-4 h-px bg-white/40" aria-hidden="true" />
            )}
            <span
              className={`px-2.5 py-1 rounded-full ${
                state === 'done'
                  ? 'bg-white/20 text-white'
                  : state === 'current'
                    ? 'bg-white text-success-700'
                    : 'bg-white/10 text-success-100'
              }`}
            >
              {state === 'done' ? '✓ ' : `${i + 1}. `}
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Confirm step before the only action that emails anyone. Loads the exact
 * recipient list approve-and-send will use, so the inspector sees who gets
 * the report before it goes (Jim 2026-09-04: "it sent it without you really
 * saying, hey, send it").
 */
/**
 * Review step of the EnviroLite finish flow (Complete → Review → Send to
 * Client). Jim 2026-09-04: the generated PDF *is* the review screen — no
 * intermediate "Ready for Review" card, no Re-generate button, and never a
 * "Back to Review" while you are already reviewing. Two actions: Edit (back
 * to the answers) and Send to Client (the only step that emails anyone, and
 * it asks first). Leaving parks the report under Unsent Reports on the
 * dashboard, so the quiet "Send to Client Later" link is the only other exit.
 */
export function ReportPhase({
  submittedInspectionId,
  siteId,
  organizationId,
  isGeneratingReport,
  reportUrl,
  reportError,
  isSendingReport,
  sendResult,
  isFinalInspection,
  generateReport,
  approveAndSend,
  sendHold = null,
  stopSend,
  onBackToDraft,
  onDone,
  onSkip,
}: ReportPhaseProps) {
  const [confirmingSend, setConfirmingSend] = useState(false);
  // JIM-1027-13: change the list while the report is queued; the send reads
  // it when the clock runs out.
  const [editingHeldRecipients, setEditingHeldRecipients] = useState(false);
  const secondsLeft = useCountdown(sendHold?.sendsAt ?? null);
  const [stoppingSend, setStoppingSend] = useState(false);
  const [tooLateToEdit, setTooLateToEdit] = useState(false);
  // Edit during the hold stops the clock first; if the send has already
  // started there is nothing left to edit.
  const editHeldReport = async () => {
    setStoppingSend(true);
    const stopped = stopSend ? await stopSend() : true;
    setStoppingSend(false);
    if (stopped) onBackToDraft();
    else setTooLateToEdit(true);
  };
  // JIM-0929-09 (Jim 2026-09-29: "when reviewing we should be able to see
  // more of the report"): the report box takes every pixel between the band
  // above it and the Edit / Send bar below, measured, so a laptop and a phone
  // both show as much of the PDF as the window allows.
  const reportBoxRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [reportHeight, setReportHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const box = reportBoxRef.current;
      if (!box) return;
      const top = box.getBoundingClientRect().top + window.scrollY;
      const actions = actionsRef.current?.offsetHeight ?? 0;
      setReportHeight(
        Math.max(MIN_REPORT_HEIGHT, window.innerHeight - top - actions)
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [sendResult, isGeneratingReport, reportUrl, sendHold, tooLateToEdit]);

  if (!sendResult) {
    const canSend =
      !!reportUrl &&
      !isGeneratingReport &&
      !isSendingReport &&
      !!submittedInspectionId;
    return (
      // In normal flow (not a fixed overlay): the sticky staging banner and the
      // Lite header keep their place above it, so the title and the "Send to
      // Client Later" link are never covered (Gate 7, 2026-09-05).
      // No min-h-screen here: under the Lite header that forced the page to
      // scroll by the header's height, which is PDF space (JIM-0929-09).
      <div className="flex flex-col bg-slate-900">
        {/* Header */}
        <div className="bg-gradient-to-r from-success-600 to-success-700 dark:from-success-700 dark:to-success-800 text-white px-4 py-2">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 min-w-0">
              <h1 className="text-base font-semibold leading-tight">
                {sendHold ? 'Sending to Client' : 'Review Report'}
              </h1>
              <StageStrip sent={false} sending={!!sendHold} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <button
                type="button"
                hidden={!!sendHold}
                onClick={() =>
                  onSkip({
                    success: true,
                    inspectionId: submittedInspectionId,
                    message:
                      'Inspection complete. The report has NOT been sent to the client yet — find it under Unsent Reports on your dashboard.',
                  })
                }
                className="text-xs font-medium text-success-100 hover:text-white underline underline-offset-2 whitespace-nowrap"
              >
                Send to Client Later
              </button>
              {/* JIM-0908-12: keep a copy on the phone without emailing anyone */}
              <button
                type="button"
                onClick={() =>
                  submittedInspectionId &&
                  inspectionService.downloadReportPdf(submittedInspectionId)
                }
                disabled={!reportUrl || isGeneratingReport}
                className="text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-md px-2 py-1 whitespace-nowrap inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5" />
                Save PDF
              </button>
              {/* JIM-0919-05: "you want to save it, click this, you want to
                  print it, click this" (Jim 2026-09-19) */}
              <button
                type="button"
                onClick={() =>
                  submittedInspectionId &&
                  inspectionService.printReportPdf(submittedInspectionId)
                }
                disabled={!reportUrl || isGeneratingReport}
                className="text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-md px-2 py-1 whitespace-nowrap inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Printer className="w-3.5 h-3.5" />
                Print PDF
              </button>
            </div>
          </div>
        </div>

        {/* The report */}
        <div
          ref={reportBoxRef}
          data-testid="review-report"
          style={{ height: reportHeight ?? undefined }}
          className="min-h-[240px] bg-slate-800 flex items-center justify-center"
        >
          {isGeneratingReport ? (
            <div className="text-center text-white px-6">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium">
                Generating Inspection Report…
              </p>
              <p className="text-sm text-slate-400 mt-2">
                Saving your answers and building the report for review.
              </p>
            </div>
          ) : reportUrl ? (
            // Every page, drawn by pdf.js: an iframe shows only page one on a
            // phone (Shane read a three-page report as one page, 2026-09-08).
            <PdfPages
              title="Inspection Report Preview"
              load={() =>
                inspectionService.getReportBytes(submittedInspectionId)
              }
              reloadKey={reportUrl}
              fallbackUrl={reportUrl}
            />
          ) : reportError ? (
            <div className="text-center text-white px-6">
              <AlertCircle className="w-12 h-12 text-danger-400 mx-auto mb-4" />
              <p className="text-lg font-medium">Report Generation Failed</p>
              <p className="text-sm text-slate-400 mt-2">
                Your inspection is saved. {reportError}
              </p>
              <button
                type="button"
                onClick={generateReport}
                className="mt-4 px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : !submittedInspectionId ? (
            <div className="text-center text-white px-6">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <p className="text-lg font-medium">Inspection Not Completed</p>
              <p className="text-sm text-slate-400 mt-2">
                The inspection did not finish completing on the server. Refresh
                the page and complete the inspection again.
              </p>
            </div>
          ) : (
            <div className="text-center text-white">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium">Loading Preview...</p>
            </div>
          )}
        </div>

        {/* JIM-1003-05: the report is on its ten-minute hold. The clock, Edit
            (which stops it) and Done (which leaves it to send on its own). */}
        {sendHold && (
          <div
            ref={actionsRef}
            data-testid="send-hold"
            className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          >
            <p
              role="status"
              className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1"
            >
              {secondsLeft > 0 ? (
                <>
                  <Clock className="w-4 h-4 text-success-600" />
                  Sending to the client in{' '}
                  <span className="tabular-nums" data-testid="send-countdown">
                    {formatCountdown(secondsLeft)}
                  </span>
                </>
              ) : (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-success-600" />
                  Sending to Client…
                </>
              )}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mb-2">
              {tooLateToEdit
                ? 'Too late to edit: the report is being sent now.'
                : secondsLeft > 0
                  ? `Nothing is emailed until ${sendsAtLabel(sendHold.sendsAt)}. Edit stops the clock; you can leave and it still sends.`
                  : 'You can leave; it finishes on its own.'}
            </p>
            {siteId && secondsLeft > 0 && !tooLateToEdit && (
              <button
                type="button"
                onClick={() => setEditingHeldRecipients(true)}
                data-testid="change-held-recipients"
                className="w-full mb-2 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
              >
                <Users className="w-4 h-4" />
                Change who gets it — applies when it sends
              </button>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={editHeldReport}
                disabled={stoppingSend || secondsLeft === 0 || tooLateToEdit}
                className="flex-1 py-3 rounded-xl font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {stoppingSend ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Pencil className="w-4 h-4" />
                )}
                Edit
              </button>
              <button
                type="button"
                onClick={() =>
                  onDone({
                    success: true,
                    inspectionId: submittedInspectionId,
                    message: `Inspection complete. The report will be emailed to the client at ${sendsAtLabel(sendHold.sendsAt)}.`,
                  })
                }
                className="flex-1 py-3 rounded-xl font-semibold bg-primary-600 hover:bg-primary-700 text-white transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Actions: Edit + Send to Client */}
        <div
          ref={sendHold ? undefined : actionsRef}
          hidden={!!sendHold}
          data-testid="review-actions"
          className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          {isSendingReport ? null : (
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mb-2">
              Nothing is emailed until you tap Send to Client.
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBackToDraft}
              disabled={isGeneratingReport || isSendingReport}
              className="flex-1 py-3 rounded-xl font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setConfirmingSend(true)}
              disabled={!canSend}
              className={`flex-1 py-3 rounded-xl font-semibold shadow-lg transition-all flex items-center justify-center gap-2 ${
                canSend
                  ? 'bg-gradient-to-r from-success-500 to-success-600 hover:from-success-600 hover:to-success-700 text-white active:scale-[0.98]'
                  : 'bg-slate-400 dark:bg-slate-600 text-white cursor-not-allowed'
              }`}
            >
              {isSendingReport ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending to Client…
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send to Client
                </>
              )}
            </button>
          </div>
        </div>

        {editingHeldRecipients && siteId && (
          <ReportRecipientsEditor
            siteId={siteId}
            organizationId={organizationId}
            inspectionId={submittedInspectionId}
            onClose={() => setEditingHeldRecipients(false)}
          />
        )}
        {confirmingSend && canSend && !sendHold && (
          <SendConfirmSheet
            inspectionId={submittedInspectionId}
            siteId={siteId}
            organizationId={organizationId}
            onConfirm={() => {
              setConfirmingSend(false);
              approveAndSend();
            }}
            onEdit={() => {
              setConfirmingSend(false);
              onBackToDraft();
            }}
          />
        )}
      </div>
    );
  }

  // Sent — the report has been emailed (or finalized with no recipients).
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-24">
      <div className="bg-gradient-to-r from-success-600 to-success-700 dark:from-success-700 dark:to-success-800 text-white px-4 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10">
            <FileText className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Sent to Client</h1>
            <p className="text-sm text-success-100">
              {sendResult.success
                ? 'The report has been emailed to the client.'
                : 'The report is finalized.'}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <StageStrip sent />
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 p-5">
          <div
            className={`p-4 rounded-lg ${
              sendResult.success
                ? 'bg-success-50 dark:bg-success-900/30 border border-success-200 dark:border-success-800'
                : 'bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-800'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle
                className={`w-5 h-5 ${
                  sendResult.success
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-warning-600 dark:text-warning-400'
                }`}
              />
              <p
                className={`font-semibold ${
                  sendResult.success
                    ? 'text-success-900 dark:text-success-100'
                    : 'text-warning-900 dark:text-warning-100'
                }`}
              >
                {sendResult.message}
              </p>
            </div>
            {sendResult.recipients && sendResult.recipients.length > 0 && (
              <div className="mt-2 space-y-1">
                {sendResult.recipients.map((r, i) => (
                  <div
                    key={i}
                    className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2"
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        r.status === 'sent' ? 'bg-success-500' : 'bg-danger-500'
                      }`}
                    />
                    {r.name} ({r.email})
                  </div>
                ))}
              </div>
            )}
            {clientSignatureLine(sendResult.clientSignature) && (
              <p
                data-testid="client-signature-line"
                className="mt-3 text-sm text-slate-700 dark:text-slate-300 border-t border-success-200 dark:border-success-800 pt-3"
              >
                {clientSignatureLine(sendResult.clientSignature)}
              </p>
            )}
            {isFinalInspection && sendResult.success && (
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 border-t border-success-200 dark:border-success-800 pt-2">
                <b>Final inspection:</b> the consultant has been asked to
                confirm. Once the consultant and the client both confirm, the
                office is notified to close the site out.
              </p>
            )}
          </div>
        </div>

        {/* JIM-0919-05: print or keep a copy right here — "here's where I'd
            say print or save file on this page" (Jim 2026-09-19). Nothing
            here emails anyone. */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() =>
              inspectionService.printReportPdf(submittedInspectionId)
            }
            className="py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium text-sm inline-flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print PDF
          </button>
          <button
            type="button"
            onClick={() =>
              inspectionService.downloadReportPdf(submittedInspectionId)
            }
            className="py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium text-sm inline-flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Save PDF
          </button>
        </div>

        <button
          type="button"
          onClick={() =>
            onDone({
              success: true,
              inspectionId: submittedInspectionId,
              message: 'Report sent to the client. Inspection complete!',
            })
          }
          className="w-full py-4 rounded-xl font-semibold bg-primary-600 hover:bg-primary-700 text-white transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}
